import type { ReactNode } from 'react'
import type { EvidenceObject, FeedItem } from '@algoboo/schema/api'
import { Icon } from '../icons/index.js'
import { fullTime, shortHash } from '../format/time.js'
import { CopyValue } from './AddressLink.js'
import { cx } from './cx.js'
import { Dialog, Eyebrow, Pill, Skeleton } from './primitives.js'

/** Footer link that opens the evidence drawer. */
export function EvidenceLink({ id, onOpen, className }: { id: string; onOpen: (id: string) => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(id)}
      className={cx('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[11px] text-ink-3 hover:bg-sunken hover:text-ink', className)}
      title={`Evidence ${id}`}
    >
      <Icon name="evidence" size={12} />
      <span>evidence</span>
      <span className="text-ink-3/80">{id.slice(0, 8)}</span>
    </button>
  )
}

export type EvidenceVerification = 'verified' | 'mismatch' | 'unchecked'

/**
 * Evidence object drawer: id (content hash), type, actor, lineage and raw
 * payload. `verification` is the client-side recomputation of the content
 * hash (apps/web does it with @algoboo/schema canonical hashing).
 */
export function EvidenceDrawer({
  open,
  onClose,
  id,
  evidence,
  loading,
  error,
  item,
  verification = 'unchecked',
  onOpenSource,
  history,
  onBack,
}: {
  open: boolean
  onClose: () => void
  id: string | null
  evidence: EvidenceObject | null | undefined
  loading?: boolean
  error?: ReactNode
  item?: FeedItem | null | undefined
  verification?: EvidenceVerification
  onOpenSource: (id: string) => void
  history?: number
  onBack?: () => void
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      side="right"
      width={560}
      title={
        <span className="flex items-center gap-2">
          {history ? (
            <button type="button" onClick={onBack} className="rounded-md p-1 text-ink-3 hover:bg-sunken hover:text-ink" aria-label="Back to previous evidence">
              <Icon name="chevron-left" size={16} />
            </button>
          ) : null}
          Evidence record
        </span>
      }
      description="Every consequential agent output is appended here before it reaches the feed. The id is the SHA-256 of the canonical JSON."
    >
      {id && (
        <div className="space-y-5">
          <div>
            <Eyebrow>Id · content hash</Eyebrow>
            <div className="mt-1 flex items-center gap-2">
              <CopyValue value={id} className="text-ink" display={id} />
            </div>
            <div className="mt-2">
              {verification === 'verified' ? (
                <Pill tone="good" icon="shield" className="h-6 normal-case tracking-normal text-[12px]">
                  Hash recomputed in your browser: matches
                </Pill>
              ) : verification === 'mismatch' ? (
                <Pill tone="bad" icon="alert" className="h-6 normal-case tracking-normal text-[12px]">
                  Hash mismatch: the payload does not hash to this id
                </Pill>
              ) : (
                <Pill tone="neutral" icon="info" className="h-6 normal-case tracking-normal text-[12px]">
                  Not yet verified
                </Pill>
              )}
            </div>
          </div>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : evidence ? (
            <>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-[13px]">
                <dt className="text-ink-3">Type</dt>
                <dd className="font-mono text-ink">{evidence.objectType}</dd>
                <dt className="text-ink-3">Actor</dt>
                <dd className="font-mono text-ink">
                  {evidence.actorKind} · <span className="break-all">{evidence.actorId}</span>
                </dd>
                <dt className="text-ink-3">Recorded</dt>
                <dd className="text-ink">
                  <time dateTime={evidence.createdAt}>{fullTime(evidence.createdAt)}</time>
                </dd>
                <dt className="text-ink-3">Schema</dt>
                <dd className="font-mono text-ink">v{evidence.schemaVersion}</dd>
                <dt className="text-ink-3">Idempotency</dt>
                <dd className="break-all font-mono text-[12px] text-ink-2">{evidence.idempotencyKey}</dd>
              </dl>
              <div>
                <Eyebrow>Lineage</Eyebrow>
                {evidence.sourceObjectIds.length === 0 ? (
                  <p className="mt-1 text-[13px] text-ink-3">Root record: nothing upstream.</p>
                ) : (
                  <ol className="mt-2 space-y-1.5">
                    {evidence.sourceObjectIds.map((s, i) => (
                      <li key={s} className="flex items-center gap-2">
                        <span className="num w-5 text-right font-mono text-[11px] text-ink-3">{i + 1}</span>
                        <button
                          type="button"
                          onClick={() => onOpenSource(s)}
                          className="inline-flex items-center gap-1 rounded-md border border-line bg-surface-2 px-2 py-1 font-mono text-[12px] text-ink-2 hover:border-line-strong hover:text-ink"
                        >
                          <Icon name="chain" size={12} />
                          {shortHash(s, 12, 8)}
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
              <RawJson label="Payload" value={evidence.payload} />
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-line-strong p-3 text-[13px] text-ink-2">
              {error ?? 'This evidence record is not available from the API yet.'}
            </div>
          )}
          {item && !evidence && <RawJson label="Post payload" value={item.payload} />}
        </div>
      )}
    </Dialog>
  )
}

export function RawJson({ label, value }: { label: string; value: unknown }) {
  return (
    <details className="group rounded-lg border border-line bg-surface-2" open>
      <summary className="flex cursor-pointer items-center justify-between px-3 py-2 text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">
        {label}
        <Icon name="chevron-down" size={14} className="transition-transform group-open:rotate-180" />
      </summary>
      <pre className="max-h-80 overflow-auto border-t border-line px-3 py-2 font-mono text-[11.5px] leading-relaxed text-ink-2">{JSON.stringify(value, null, 2)}</pre>
    </details>
  )
}
